// Aucune importation ici, volontairement : ce fichier est tiré par le template
// d'options de routeur, lui-même pris dans la chaîne des route rules. Une feuille
// sans dépendance ne peut pas refermer de cycle.

export interface TenantRoute {
  path: string;
  name?: string | symbol | null;
  [key: string]: unknown;
}

export interface TenantRoutesOptions {
  /** Segment dynamique portant le tenant, ex. `site` pour `pages/[site]/`. */
  tenantDynamicRoute: string;
  /** Domaines racines servis sans tenant. */
  rootDomains: string[];
  /** Retire le domaine racine plutôt que de prendre le premier label. */
  strictSubdomains: boolean;
  /** Routes statiques réservées à un tenant, exclues du domaine racine. */
  sites: string[];
  /** Association hôte complet → tenant, pour les domaines propres à un client. */
  customDomains: Record<string, string>;
}

function rewritePrefixRoute<T extends TenantRoute>(route: T, prefix: string): T {
  if (route.path.startsWith(prefix)) {
    return { ...route, path: route.path.replace(prefix, "") };
  }
  return route;
}

/** Restreint la table de routes au tenant déduit de `hostname`. */
export function filterRoutesForHost<T extends TenantRoute>(
  routes: T[],
  hostname: string,
  options: TenantRoutesOptions,
): T[] {
  const { tenantDynamicRoute, rootDomains, strictSubdomains, customDomains } = options;
  const sites = new Set(options.sites);

  const dynamicRoutePrefix = `/:${tenantDynamicRoute}()`;
  const ignoreDynamicRoute = (route: T) => !route.path.startsWith(dynamicRoutePrefix);
  const ignoreTenantSitesRoute = (route: T) => !sites.has(route.name as string);

  const customDomainRoute = customDomains[hostname];
  if (customDomainRoute) {
    return routes
      .filter(ignoreDynamicRoute)
      .filter(route => route.path.startsWith("/" + customDomainRoute))
      .map(route => rewritePrefixRoute(route, "/" + customDomainRoute));
  }

  const rootDomain = rootDomains.find(domain => hostname.endsWith(domain));
  if (!rootDomain) {
    return routes;
  }
  if (hostname === rootDomain) {
    return routes.filter(ignoreDynamicRoute).filter(ignoreTenantSitesRoute);
  }

  const tenant = strictSubdomains
    ? hostname.substring(0, hostname.indexOf(rootDomain) - 1)
    : hostname.split(".")[0];

  if (sites.has(tenant)) {
    return routes
      .filter(ignoreDynamicRoute)
      .filter(route => route.path.startsWith("/" + tenant))
      .map(route => rewritePrefixRoute(route, "/" + tenant));
  }

  return routes.map(route => rewritePrefixRoute(route, dynamicRoutePrefix));
}
